import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PasswordInputWithToggle } from "../components/PasswordInputWithToggle";
import { useAuth } from "../context/AuthContext";
import { isAuthenticatedPublisher } from "../services/authAccess";
import {
  checkPublisherLoginEligibility,
  hasValidPublisherLoginEmailFormat,
  isNonPublisherAccountLoginError,
  normalizePublisherLoginInput,
  PUBLISHER_LOGIN_PRECHECK_MESSAGE,
  resolveAuthErrorMessage,
  validatePublisherLoginInput,
} from "../services/publisherAuth";

type LogInFormValues = {
  email: string;
  password: string;
};

type LogInLocationState = {
  registered?: boolean;
  email?: string;
  from?: string;
};

const initialValues: LogInFormValues = {
  email: "",
  password: "",
};

export function LogInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout } = useAuth();
  const locationState = (location.state as LogInLocationState | null) ?? null;
  const [values, setValues] = useState<LogInFormValues>(initialValues);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [precheckMessage, setPrecheckMessage] = useState("");

  useEffect(() => {
    if (locationState?.registered) {
      setFormSuccess("Your publisher account is ready. Please log in.");
    }
    if (locationState?.email) {
      setValues((current) => ({ ...current, email: locationState.email ?? "" }));
    }
  }, [locationState?.email, locationState?.registered]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "email") {
      setPrecheckMessage("");
    }

    setValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  };

  async function handlePasswordFocus() {
    if (!hasValidPublisherLoginEmailFormat(values.email)) {
      setPrecheckMessage("");
      return;
    }

    try {
      const eligible = await checkPublisherLoginEligibility(values.email);
      setPrecheckMessage(
        eligible ? "" : PUBLISHER_LOGIN_PRECHECK_MESSAGE,
      );
    } catch {
      setPrecheckMessage("");
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }

    setFormError("");
    setFormSuccess("");
    setPrecheckMessage("");

    const errors = validatePublisherLoginInput(values);
    if (errors.length > 0) {
      setValidationErrors([errors[0]]);
      return;
    }

    setValidationErrors([]);

    setSubmitting(true);

    try {
      const normalized = normalizePublisherLoginInput(values);
      const nextUser = await login(normalized.email, normalized.password);
      if (!isAuthenticatedPublisher(nextUser)) {
        logout();
        navigate("/sign-in", { replace: true });
        return;
      }
      navigate(locationState?.from ?? "/publisher-dashboard", { replace: true });
    } catch (error: unknown) {
      setValidationErrors([]);
      if (isNonPublisherAccountLoginError(error)) {
        navigate("/sign-in", { replace: true });
        return;
      }
      setFormError(resolveAuthErrorMessage(error, "login"));
    } finally {
      setSubmitting(false);
    }
  }

  const errorMessage = formError || validationErrors[0] || "";
  const validationMessage =
    errorMessage ||
    precheckMessage ||
    (formSuccess && !formError ? formSuccess : "");
  const isValidationError = Boolean(errorMessage);

  return (
    <article className="oac-screen-05 oac-log-in-page">
      <section className="oac-log-in-page__copy">
        <h1 className="oac-log-in-page__heading">Congratulations!</h1>

        <div className="oac-log-in-page__message">
          <p>Your publisher account is ready.</p>
          <p>Login to start publishing your apps.</p>
        </div>

        <form className="oac-log-in-form" onSubmit={handleSubmit}>
          <div className="oac-log-in-form__fields">
            <div className="oac-log-in-form__field">
              <label htmlFor="log-in-email">Email</label>

              <input
                autoComplete="email"
                id="log-in-email"
                name="email"
                onChange={handleChange}
                required
                type="email"
                value={values.email}
              />
            </div>

            <div className="oac-log-in-form__field">
              <label htmlFor="log-in-password">Password</label>

              <PasswordInputWithToggle
                autoComplete="current-password"
                id="log-in-password"
                name="password"
                onChange={handleChange}
                onFocus={handlePasswordFocus}
                required
                value={values.password}
              />
            </div>
          </div>

          <div
            aria-live="polite"
            className="oac-log-in-form__validation"
          >
            {validationMessage ? (
              <p
                className={
                  isValidationError
                    ? "oac-form-message oac-form-message--error"
                    : "oac-form-message"
                }
              >
                {validationMessage}
              </p>
            ) : null}
          </div>

          <div className="oac-log-in-form__actions">
            <button
              aria-busy={submitting}
              className="oac-log-in-form__submit"
              disabled={submitting}
              type="submit"
            >
              Log In
            </button>
          </div>
        </form>
      </section>

      <section className="oac-log-in-page__panel-region">
        <div
          aria-label="Publisher account created successfully"
          className="oac-log-in-success"
        >
          <div aria-hidden="true" className="oac-log-in-success__check-circle">
            <svg className="oac-log-in-success__check" fill="none" viewBox="0 0 72 72">
              <path
                d="M17 37L30 50L56 24"
                stroke="currentColor"
                strokeLinecap="square"
                strokeLinejoin="miter"
                strokeWidth="7"
              />
            </svg>
          </div>

          <p className="oac-log-in-success__label">Success!</p>
        </div>
      </section>
    </article>
  );
}
